import React, { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { normalizeUrl } from '../../Elements/UI/fileLink'
import ConfirmModal from '../../Elements/UI/ConfirmModal'
import {
  instalmentBreakdown,
  formatINR,
  formatDate,
  hasDue,
  uploadReceipt,
  deleteReceipt
} from '../../../features/payments/memberPayments'

const statusMeta = {
  paid: { label: 'Paid', chip: 'bl-chip--success', icon: 'checkmark-circle-outline' },
  partial: { label: 'Partly paid', chip: 'bl-chip--warning', icon: 'remove-circle-outline' },
  due: { label: 'Not paid', chip: 'bl-chip--danger', icon: 'alert-circle-outline' },
  na: { label: '—', chip: 'bl-chip--neutral', icon: 'ellipse-outline' }
}

// Full breakdown for one payment entry (a member can have an additional-area
// entry and an additional-flat entry).
//  - isAdmin: shows Edit, receipt upload and receipt delete (members can only view receipts)
//  - onChange(updatedEntry): called after a receipt is added / deleted
//  - onEdit(entry): admin "Edit" button
const PaymentPlan = ({ entry, isAdmin = false, onChange, onEdit }) => {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [receiptToDelete, setReceiptToDelete] = useState(null)

  const instalments = instalmentBreakdown(entry)
  const receipts = entry.receipts || []
  const progress = entry.totalPayable ? Math.min(100, Math.round((entry.totalReceived / entry.totalPayable) * 100)) : 0
  const due = hasDue(entry)
  const inputId = `receipt-${entry.id}`

  const handleFile = async e => {
    const file = e.target.files?.[0]
    if (!file) return
    // an old backend sends rows without an id; don't call /payments/undefined/...
    if (entry.id == null) {
      toast.error('Payment data is out of date. Please refresh the page and try again.')
      e.target.value = ''
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large (max 10 MB)')
      e.target.value = ''
      return
    }
    setUploading(true)
    try {
      const receipt = await uploadReceipt(entry.id, file)
      onChange && onChange({ ...entry, receipts: [...receipts, receipt] })
      toast.success(isAdmin ? 'Receipt added. The member can now see it.' : 'Receipt uploaded.')
    } catch (err) {
      console.error('Receipt upload failed', err)
      toast.error(err.response?.data?.message || 'Receipt upload failed. Please try again.')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const removeReceipt = async () => {
    const target = receiptToDelete
    if (!target) return
    try {
      await deleteReceipt(target.id)
      onChange && onChange({ ...entry, receipts: receipts.filter(r => r.id !== target.id) })
      setReceiptToDelete(null)
      toast.success('Receipt deleted')
    } catch (err) {
      console.error(err)
      toast.error(err.response?.data?.message || 'Failed to delete receipt')
    }
  }

  return (
    <div className="bl-pay">
      <div className="card bl-pay__summary">
        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
          <span className={`bl-chip ${entry.type === 'Additional flat' ? 'bl-chip--info' : 'bl-chip--primary'}`}>
            <ion-icon name={entry.type === 'Additional flat' ? 'business-outline' : 'resize-outline'} aria-hidden="true"></ion-icon>
            {entry.type}
          </span>
          <span className="text-muted small">Flat {entry.flatNo}</span>
          {isAdmin && onEdit && (
            <button type="button" className="btn btn-secondary btn-sm ms-auto" onClick={() => onEdit(entry)}>
              <ion-icon name="create-outline" aria-hidden="true"></ion-icon>
              Edit payment
            </button>
          )}
        </div>

        <div className="bl-pay__stats">
          <div>
            <span className="bl-pay__label">Area opted</span>
            <span className="bl-pay__value">{entry.areaSqft} sq.ft</span>
          </div>
          <div>
            <span className="bl-pay__label">Cost of area</span>
            <span className="bl-pay__value">{formatINR(entry.areaCost)}</span>
          </div>
          <div>
            <span className="bl-pay__label">Payable so far (4 × 5%)</span>
            <span className="bl-pay__value bl-pay__value--strong">{formatINR(entry.totalPayable)}</span>
          </div>
        </div>

        <div className="bl-pay__progress">
          <div className="d-flex justify-content-between small mb-1 flex-wrap gap-1">
            <span>Received <strong>{formatINR(entry.totalReceived)}</strong></span>
            {due
              ? <span className="text-danger">Outstanding <strong>{formatINR(entry.outstanding)}</strong></span>
              : <span className="text-success"><strong>No dues</strong></span>}
          </div>
          <div className="bl-pay__bar" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label={`${progress}% of payable amount received`}>
            <span style={{ width: `${progress}%` }}></span>
          </div>
          {entry.advance > 0 && (
            <div className="bl-pay__advance">
              <ion-icon name="trending-up-outline" aria-hidden="true"></ion-icon>
              Advance paid: <strong>{formatINR(entry.advance)}</strong>
              <span className="text-muted"> (paid ahead, e.g. towards the 5th instalment)</span>
            </div>
          )}
        </div>
      </div>

      <h3 className="section-title px-0">Instalments</h3>
      <ol className="bl-pay__list">
        {instalments.map(inst => {
          const meta = statusMeta[inst.status]
          return (
            <li key={inst.no} className={`card bl-pay__item is-${inst.status === 'paid' ? 'paid' : inst.status === 'partial' ? 'due' : inst.status === 'due' ? 'overdue' : 'na'}`}>
              <div className="bl-pay__item-main">
                <div className="bl-pay__item-title">Instalment {inst.no} <span className="text-muted fw-normal">(5%)</span></div>
                <div className="bl-pay__item-meta">
                  Paid {formatINR(inst.paid)}
                  {inst.balance >= 1 && <span className="text-danger"> · Balance {formatINR(inst.balance)}</span>}
                </div>
                <span className={`bl-chip ${meta.chip} mt-2`}>
                  <ion-icon name={meta.icon} aria-hidden="true"></ion-icon>
                  {meta.label}
                </span>
              </div>
              <div className="bl-pay__item-side">
                <div className="bl-pay__amount">{formatINR(inst.amount)}</div>
              </div>
            </li>
          )
        })}
      </ol>

      <div className="card bl-pay__receipts">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <div className="bl-pay__item-title">Payment receipts</div>
            <div className="bl-pay__item-meta">
              {isAdmin
                ? 'Receipts you add here are visible to this member.'
                : 'Receipts added by the society office for your payments.'}
            </div>
          </div>
          {isAdmin && (
            <div>
              <label htmlFor={inputId} className="visually-hidden">Upload payment receipt</label>
              <input id={inputId} ref={inputRef} type="file" accept="image/*,application/pdf" className="d-none" onChange={handleFile} />
              <button type="button" className="btn btn-primary btn-sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
                {uploading
                  ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                  : <ion-icon name="cloud-upload-outline" aria-hidden="true"></ion-icon>}
                {uploading ? 'Uploading…' : 'Upload receipt'}
              </button>
            </div>
          )}
        </div>

        {receipts.length === 0 ? (
          <p className="text-muted small mb-0 mt-3">{isAdmin ? 'No receipts yet.' : 'No receipt added yet.'}</p>
        ) : (
          <ul className="bl-pay__receipt-list">
            {receipts.map(r => (
              <li key={r.id}>
                <a href={normalizeUrl(r.url)} target="_blank" rel="noopener noreferrer">
                  <ion-icon name="receipt-outline" aria-hidden="true"></ion-icon>
                  {r.name || 'Receipt'}
                </a>
                <span className="text-muted small flex-grow-1">
                  {formatDate(r.uploadedOn)} · {r.uploadedByAdmin ? 'Society office' : 'Member'}
                </span>
                {isAdmin && (
                  <button
                    type="button"
                    className="btn btn-icon btn-sm"
                    onClick={() => setReceiptToDelete(r)}
                    aria-label={`Delete receipt ${r.name || ''}`}
                    title="Delete receipt"
                  >
                    <ion-icon name="trash-outline" aria-hidden="true"></ion-icon>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        show={!!receiptToDelete}
        onClose={() => setReceiptToDelete(null)}
        onConfirm={removeReceipt}
        title="Delete this receipt?"
        message={receiptToDelete ? `“${receiptToDelete.name || 'Receipt'}” will be removed.` : ''}
        confirmLabel="Delete"
      />
    </div>
  )
}

export default PaymentPlan
